import React, { PureComponent } from 'react'
import {
  string, func, bool, shape,
} from 'prop-types'
import noop from 'lodash/noop'
import isEmpty from 'lodash/isEmpty'
import throttle from 'lodash/throttle'
import classNames from 'classnames'
import { withI18n } from '@active/react-utils/i18n'
import { success, error } from '@active/react-ui/FloatAlert'
import DefaultComponentLayout, { LayoutContent } from '../common/Layout'
import I18nMessage from '../common/I18nMessage'
import AuthenticationItem from './components/AuthenticationItem'
import { calculateCounter } from '../common/MFAContent/MFAContent'
import { maskSenderEmail, maskSenderPhone } from '../../shared/utils/commonUtils'
import ProxyModalWithBodyDesc, { VerifyCodeOnly, VerifyCodeByPhone } from '../common/ProxyModalWithBodyDesc'
import './SecuritySetting.scss'

const PHONE_MODE = 'PHONE_MODE'
const EMAIL_MODE = 'EMAIL_MODE'
const PHONE_MODE_CONFIG = 'PHONE_MODE_CONFIG'
const EMAIL_MODE_CONFIG = 'EMAIL_MODE_CONFIG'
const modeStateMapping = {
  [PHONE_MODE]: PHONE_MODE_CONFIG,
  [EMAIL_MODE]: EMAIL_MODE_CONFIG,
}
const primaryLabel = 'passport.wallet.primary.your'
const nonPrimaryLable = 'passport.security.setting.option.set.button'
const verifyEmail = 'passport.security.setting.email.verify'
const changePhone = 'passport.security.setting.phone.change'
const setUpPhone = 'passport.security.setting.phone.setUp'
class SecuritySetting extends PureComponent {
  static propTypes = {
    i18n: func.isRequired,
    configureTrack: func.isRequired,
    services: shape({
      setPrimaryChannel: func.isRequired,
      sendCode: func.isRequired,
      submitChannel: func.isRequired,
    }).isRequired,
    emailInfo: shape({
      address: string.isRequired,
      isPrimary: bool,
      isVerified: bool,
    }).isRequired,
    phoneInfo: shape({
      number: string,
      isPrimary: bool,
    }),
    latestPhone: string,
    nextTimeEmail: string,
    nextTimePhone: string,
    kcErrorMessage: string,
    kcMessageType: string,
    stateChecker: string,
    referrer: string,
    locale: string,
  }

  static defaultProps = {
    locale: 'en_US',
    phoneInfo: {},
    kcErrorMessage: undefined,
    kcMessageType: undefined,
    nextTimeEmail: undefined,
    nextTimePhone: undefined,
    latestPhone: undefined,
  }

  constructor(props) {
    super(props)
    const {
      emailInfo, phoneInfo, nextTimeEmail,
      kcErrorMessage, kcMessageType,
    } = props
    this.state = {
      firstSendForEmailMode: !nextTimeEmail,
      firstOpenForPhoneMode: true,
      emailInfo,
      phoneInfo,
      shown: false,
      currentModal: {},
      messageContent: kcErrorMessage,
      messageType: kcMessageType,
      [EMAIL_MODE_CONFIG]: {},
      [PHONE_MODE_CONFIG]: {},
    }
    this.handleStartChangePhone = this.handleStartChangePhone.bind(this)
    this.handleStartVerifyEmail = this.handleStartVerifyEmail.bind(this)
    this.setPrimaryWithExtra = this.setPrimaryWithExtra.bind(this)
    this.getConfigOfPhoneAuthentication = this.getConfigOfPhoneAuthentication.bind(this)
    this.getConfigOfEmailAuthentication = this.getConfigOfEmailAuthentication.bind(this)
    this.handleCancelSetting = this.handleCancelSetting.bind(this)
    this.handleSubmit = this.handleSubmit.bind(this)
    this.handleSendCode = throttle(this.handleSendCode.bind(this), 2000, { trailing: false })
    this.getModalConfig = this.getModalConfig.bind(this)
    this.proxyForm = this.proxyForm.bind(this)
    this.getChannelInfo = this.getChannelInfo.bind(this)
    this.updateCount = this.updateCount.bind(this)
    this.cleanState = this.cleanState.bind(this)
  }

  componentDidMount() {
    const { configureTrack, nextTimeEmail, nextTimePhone } = this.props
    nextTimeEmail && this.updateCount(nextTimeEmail, EMAIL_MODE)
    nextTimePhone && this.updateCount(nextTimePhone, PHONE_MODE)
    configureTrack({
      eventName: 'SecuritySetting',
      widget: 'securitysetting',
      pageType: 'securitysetting',
    })
  }

  handleStartChangePhone() {
    const { firstOpenForPhoneMode: prefillPhone } = this.state
    const { latestPhone } = this.props
    this.setState({ shown: true, currentModal: this.getModalConfig(PHONE_MODE), firstOpenForPhoneMode: false }, () => {
      if (prefillPhone && latestPhone) {
        const currentForm = this.formContainer[PHONE_MODE]
        setTimeout(() => {
          currentForm.setValues({
            phoneNumber: latestPhone,
          }, false)
        }, 0)
      }
    })
  }

  async handleSendCode() {
    const channelInfos = await this.getChannelInfo()
    if (isEmpty(channelInfos)) {
      return
    }
    const { destination, channelType } = channelInfos
    const {
      services: { sendCode }, i18n, stateChecker, referrer,
    } = this.props
    sendCode({
      params: {
        channelType,
        channelValue: destination,
        stateChecker,
        referrer,
      },
      resolve: ({
        status, messageContent, nextTimeWindow,
      }) => {
        if (status === 'SUCCESS') {
          const { currentModal: { type } = {} } = this.state
          if (type === PHONE_MODE) {
            this.setState(state => ({
              PHONE_MODE_CONFIG: {
                ...state.PHONE_MODE_CONFIG,
                showSentCodeWording: true,
              },
            }))
          }
          this.updateCount(nextTimeWindow)
          messageContent && success({ timeout: 4000, message: messageContent, theme: 'inline' })
        } else {
          messageContent && error({ timeout: 4000, message: messageContent, theme: 'inline' })
        }
      },
      reject: ({ message: { messageContent } = {} }) => {
        error({ timeout: 4000, message: messageContent || i18n('passport.error.server.default'), theme: 'inline' })
      },
    })
  }

  handleStartVerifyEmail() {
    const { firstSendForEmailMode } = this.state
    this.setState({ currentModal: this.getModalConfig(EMAIL_MODE) }, () => {
      if (firstSendForEmailMode) {
        this.handleSendCode()
        this.setState({ firstSendForEmailMode: false })
      }
      this.setState({ shown: true })
    })
  }

  handleCancelSetting() {
    this.setState(state => ({
      currentModal: {
        ...state.currentModal,
        type: undefined,
        invisible: 'true',
      },
    }))
  }

  async handleSubmit() {
    const { currentModal: { type } = {} } = this.state
    const currentForm = this.formContainer[type]
    const result = await currentForm.validateForm({ shouldTouchFields: true })
    if (isEmpty(result)) {
      const {
        services: { submitChannel }, i18n, stateChecker, referrer,
      } = this.props
      const { destination, channelType } = await this.getChannelInfo()
      const code = currentForm.values?.code
      submitChannel({
        params: {
          channelType,
          channelValue: destination,
          code: code,
          stateChecker,
          referrer,
        },
        resolve: ({
          status, messageContent, emailInfo, phoneInfo,
        }) => {
          if (status === 'SUCCESS') {
            this.setState(state => ({
              emailInfo: emailInfo || state.emailInfo,
              phoneInfo: phoneInfo || state.phoneInfo,
            }))
            success({ timeout: 4000, message: messageContent || i18n('passport.security.setting.operate.success'), theme: 'inline' })
            this.cleanState()
          } else {
            messageContent && error({ timeout: 4000, message: messageContent, theme: 'inline' })
          }
        },
        reject: ({ message: { messageContent } = {} }) => {
          error({ timeout: 4000, message: messageContent || i18n('passport.error.server.default'), theme: 'inline' })
        },
      })
    }
  }

  getChannelInfo() {
    return new Promise(resolve => {
      const { currentModal: { type } = {}, emailInfo } = this.state
      let destination
      let channelType
      if (type === EMAIL_MODE) {
        destination = emailInfo.address
        channelType = 'EMAIL'
      } else {
        const res = this.formContainer[type].setFieldTouched('phoneNumber', true)
        if (res.phoneNumber) resolve({})
        destination = this.formContainer[type].values?.phoneNumber.replace(/\s*/g, '')
        channelType = 'SMS'
      }
      resolve({ destination, channelType })
    })
  }

  getModalConfig(type) {
    const { i18n } = this.props
    const configs = { type, invisible: 'false' }
    const {
      emailInfo: {
        address,
      } = {},
      phoneInfo: {
        number: phoneNumber,
      } = {},
    } = this.state
    if (type === EMAIL_MODE) {
      configs.title = i18n('passport.security.setting.email.verify')
      configs.desc = <I18nMessage id="passport.security.setting.verify.email.modal.description" params={{ 0: maskSenderEmail(address) }} />
      configs.okText = 'passport.common.btn.verify'
    } else if (type === PHONE_MODE && !!phoneNumber) {
      configs.title = i18n('passport.security.setting.phone.change')
      configs.desc = i18n('passport.security.setting.change.phone.modal.description')
    } else {
      configs.title = i18n('passport.security.setting.modal.phone.setUp')
      configs.desc = i18n('passport.security.setting.change.phone.modal.description')
    }
    return configs
  }

  getConfigOfPhoneAuthentication() {
    const { phoneInfo: { number: phoneNumber, isPrimary } = {} } = this.state
    const markConfig = phoneNumber ? {
      name: isPrimary ? primaryLabel : nonPrimaryLable,
      isPrimary,
      triggerCallback: isPrimary ? noop : this.setPrimaryWithExtra(phoneNumber, 'SMS'),
    } : {}
    const operatorConfig = {
      name: phoneNumber
        ? changePhone
        : setUpPhone,
      triggerCallback: this.handleStartChangePhone,
    }
    return { markConfig, operatorConfig, contact: maskSenderPhone(phoneNumber) }
  }

  getConfigOfEmailAuthentication() {
    const {
      emailInfo: {
        isVerified, address, isPrimary,
      } = {},
    } = this.state
    const markConfig = isVerified ? {
      name: isPrimary ? primaryLabel : nonPrimaryLable,
      isPrimary,
      triggerCallback: isPrimary ? noop : this.setPrimaryWithExtra(address, 'EMAIL'),
    } : {}
    const operatorConfig = isVerified ? {} : { name: verifyEmail, triggerCallback: this.handleStartVerifyEmail }
    return { operatorConfig, markConfig, contact: maskSenderEmail(address) }
  }

  setPrimaryWithExtra(contact, primaryChannel) {
    return () => {
      const {
        services: { setPrimaryChannel }, i18n, stateChecker, referrer,
      } = this.props
      setPrimaryChannel({
        params: {
          channelValue: contact,
          primaryChannel,
          stateChecker,
          referrer,
        },
        resolve: ({
          status, messageContent, emailInfo, phoneInfo,
        }) => {
          if (status === 'SUCCESS') {
            success({ timeout: 4000, message: messageContent || i18n('passport.security.setting.primary.updated'), theme: 'inline' })
            this.setState(state => ({
              emailInfo: emailInfo || state.emailInfo,
              phoneInfo: phoneInfo || state.phoneInfo,
            }))
          } else {
            messageContent && error({ timeout: 4000, message: messageContent, theme: 'inline' })
          }
        },
        reject: ({ message: { messageContent } = {} }) => {
          error({ timeout: 4000, message: messageContent || i18n('passport.error.server.default'), theme: 'inline'})
        },
      })
    }
  }

  proxyForm = mode => form => {
    this.formContainer = this.formContainer || {}
    this.formContainer[mode] = form
  }

  cleanState() {
    const { currentModal: { type } = {} } = this.state
    if (type === PHONE_MODE) {
      this.setState(state => ({
        PHONE_MODE_CONFIG: {
          ...state.PHONE_MODE_CONFIG,
          showSentCodeWording: false,
        },
      }))
    }
    this.formContainer[type].resetForm()
    this.handleCancelSetting()
  }

  updateCount(nextTimeWindow, defaultType) {
    const { currentModal: { type: currentType } = {} } = this.state
    const type = defaultType || currentType
    this[type] = setInterval(() => {
      const countDownNumber = calculateCounter(nextTimeWindow)
      if (countDownNumber > 0) {
        this.setState(prvState => ({
          [modeStateMapping[type]]: {
            ...prvState[modeStateMapping[type]],
            countDownNumber: countDownNumber,
          },
        }))
      } else {
        clearInterval(this[type])
        this.setState(prvState => ({
          [modeStateMapping[type]]: {
            ...prvState[modeStateMapping[type]],
            countDownNumber: undefined,
          },
        }))
      }
    }, 1000)
  }

  render() {
    const {
      shown, messageContent, messageType, currentModal,
      EMAIL_MODE_CONFIG: EMAIL_MODE_CONFIG_STATE, PHONE_MODE_CONFIG: PHONE_MODE_CONFIG_STATE,
    } = this.state
    const { locale } = this.props
    return (
      <DefaultComponentLayout nameSpace="passport__security-setting" messageType={messageType} errMsg={messageContent}>
        <LayoutContent title message="passport.securitySetting.title" />
        <LayoutContent main>
          <div className="security-setting-introduction">
            <I18nMessage id="passport.security.setting.introduction" />
          </div>
          <div className="security-setting-subtitle">
            <I18nMessage id="passport.security.setting.subTitle" />
          </div>
          <div className="security-setting-protect-description">
            <I18nMessage id="passport.security.setting.protect.description" />
          </div>
          <div className="security-setting-authentication">
            <AuthenticationItem
              authenticationName="passport.security.setting.option.email.validation"
              verificationCode="passport.security.setting.email.verification.code"
              {...this.getConfigOfEmailAuthentication()}
            />
            <AuthenticationItem
              authenticationName="passport.security.setting.option.phone.validation"
              verificationCode="passport.security.setting.phone.verification.code"
              {...this.getConfigOfPhoneAuthentication()}
            />
          </div>
          <ProxyModalWithBodyDesc
            {...currentModal}
            shown={shown}
            className="security-settings-modal"
            size="md"
            onClose={this.handleCancelSetting}
            handleCancel={this.handleCancelSetting}
            handleSubmit={this.handleSubmit}
          >
            <div className="modal-body-content">
              <div className={classNames('verify-email-modal-content', { hidden: currentModal.type !== EMAIL_MODE })}>
                <VerifyCodeOnly
                  onSendCode={this.handleSendCode}
                  proxyForm={this.proxyForm(EMAIL_MODE)}
                  {...EMAIL_MODE_CONFIG_STATE}
                />
              </div>
              <div className={classNames('phone-modal-content', { hidden: currentModal.type !== PHONE_MODE })}>
                <VerifyCodeByPhone
                  locale={locale}
                  onSendCode={this.handleSendCode}
                  proxyForm={this.proxyForm(PHONE_MODE)}
                  {...PHONE_MODE_CONFIG_STATE}
                />
              </div>
            </div>
          </ProxyModalWithBodyDesc>
        </LayoutContent>
      </DefaultComponentLayout>
    )
  }
}

export default withI18n(SecuritySetting)
